import React, { useState, useEffect } from 'react';
import { X, Sparkles, Save, FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';

interface DescriptionEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (description: string) => void;
  categoryName: string;
  categoryColor: string;
  initialDescription: string;
}

const DescriptionEditorModal: React.FC<DescriptionEditorModalProps> = ({
  isOpen,
  onClose,
  onSave,
  categoryName,
  categoryColor,
  initialDescription,
}) => {
  const [description, setDescription] = useState(initialDescription);
  const maxLength = 10000;

  useEffect(() => {
    if (isOpen) {
      setDescription(initialDescription);
    }
  }, [isOpen, initialDescription]);

  const handleSave = () => {
    onSave(description);
    onClose();
  };

  const characterCount = description.length;
  const percentUsed = (characterCount / maxLength) * 100;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0">
        {/* Header */}
        <DialogHeader className="flex-shrink-0 p-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div 
                className="w-4 h-4 rounded"
                style={{ backgroundColor: categoryColor }}
              />
              <div>
                <DialogTitle className="text-xl">
                  {categoryName} - Category Description
                </DialogTitle>
                <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                  <Sparkles className="w-3 h-3 text-purple-500" />
                  This description will be used by AI to review your portfolio
                </p>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Tips Section */}
        <div className="flex-shrink-0 px-6 py-3 bg-purple-50 border-b">
          <div className="flex items-start gap-2">
            <FileText className="w-4 h-4 text-purple-600 mt-0.5" />
            <div className="text-sm text-purple-800">
              <strong>Tips for a good description:</strong>
              <ul className="mt-1 space-y-1 text-purple-700">
                <li>• Define the purpose and goals of this category</li>
                <li>• Specify position sizing rules (e.g., max 5% per position)</li>
                <li>• Describe entry/exit criteria and timeframes</li>
                <li>• Include risk management rules (stop-loss levels, max drawdown)</li>
                <li>• Note any sector preferences or restrictions</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Editor Area */}
        <div className="flex-1 p-6 overflow-hidden flex flex-col">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={`Describe the rules and meaning of the "${categoryName}" category...

Example:
"${categoryName}" positions are for [purpose]. 

Position Sizing Rules:
- Maximum allocation per position: X%
- Minimum conviction required before entry: [criteria]

Entry Criteria:
- [Your entry rules]

Exit Criteria:
- Stop-loss at X% below entry
- Profit booking rules

Risk Management:
- Maximum category drawdown: X%
- [Other rules]`}
            className="flex-1 resize-none text-base leading-relaxed p-4"
            maxLength={maxLength}
          />
          
          {/* Character Count */}
          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-48 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all ${
                    percentUsed > 90 ? 'bg-red-500' : 
                    percentUsed > 70 ? 'bg-yellow-500' : 'bg-purple-500'
                  }`}
                  style={{ width: `${Math.min(percentUsed, 100)}%` }}
                />
              </div>
              <span className={`text-sm ${
                percentUsed > 90 ? 'text-red-600' : 'text-gray-500'
              }`}>
                {characterCount.toLocaleString()} / {maxLength.toLocaleString()} characters
              </span>
            </div>
            
            {characterCount < 100 && (
              <span className="text-sm text-amber-600">
                💡 More detail helps AI provide better analysis
              </span>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-6 pt-4 border-t bg-gray-50 flex items-center justify-between">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <div className="flex items-center gap-3">
            {description !== initialDescription && (
              <span className="text-sm text-amber-600">Unsaved changes</span>
            )}
            <Button 
              onClick={handleSave}
              className="gap-2 bg-purple-600 hover:bg-purple-700"
            >
              <Save className="w-4 h-4" />
              Save Description
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DescriptionEditorModal;
